const FIELD_SECTION_PRINCIPAL = "section_1_principal";
const FIELD_SECTION_SECONDARY = "section_2_secondary";
export const SECTION_TYPE_PRINCIPAL = "principal";
export const SECTION_TYPE_SECONDARY = "secondary";

/**
 * Valida de forma ultra rápida O(1) si la cabecera de la sección está activa
 * @param {Array} list - El arreglo (arrayListPrincipal o arrayListSecondary)
 * @param {boolean} isPreviewMode - true evalúa preview, false evalúa full
 * @returns {boolean}
 */
export const isSectionVisible = (list, isPreviewMode = false) => {
  // Guard preliminar: si la lista no existe o está vacía, no es visible
  if (!list || list.length === 0) return false;

  // Acceso directo al índice 0 con encadenamiento opcional para evitar rupturas
  const headerSection = list[0];

  return isPreviewMode 
    ? headerSection?.show_view_preview === true 
    : headerSection?.show_view_full === true;
};

export const isSectionNewEditVisible = (list) => {
  // Guard preliminar: si la lista no existe o está vacía, no es visible
  if (!list || list.length === 0) return false;

  const headerSection = list.find((item) => item?.type === 'section') || list[0];
  const rawPre = headerSection?.new_edit_premanager;
  const headerPreFlag = rawPre === true || String(rawPre).toLowerCase() === 'true';
  const anyPreFlag = list.some((item) => {
    const value = item?.new_edit_premanager;
    return value === true || String(value).toLowerCase() === 'true';
  });

  return headerSection?.show_new === true || headerPreFlag || anyPreFlag;
};

/**
 * Obtiene de forma segura el objeto de cabecera de la sección
 * @param {Array} list 
 * @returns {Object|null} El objeto de cabecera o null si falla
 */
export const getSectionHeader = (list) => {
  if (!list || list.length === 0) return null;
  return list.find((item) => item?.type === 'section') || list[0];
};

/**
 * Valida si una sección debe mostrarse buscando un objeto de cabecera específico 
 * y evaluando su propiedad de visibilidad.
 * 
 * @param {Array} list - El arreglo de configuración (arrayListPrincipal o arrayListSecondary)
 * @param {string} sectionType - El tipo de lista: 'principal' o 'secondary'
 * @param {boolean} isPreviewMode - true evalúa show_view_preview, false evalúa show_view_full
 * @returns {boolean} true si el objeto objetivo tiene su flag en true, false en caso contrario
 */
export const showAccordionSection = (list, sectionType = SECTION_TYPE_PRINCIPAL, isPreviewMode = false) => {
  if (!list || list.length === 0) return false;

  // 1. Determinar el nombre del campo 'field' que debemos buscar como objetivo
  const targetField = sectionType === 'principal' ? FIELD_SECTION_PRINCIPAL : FIELD_SECTION_SECONDARY;

  // 2. Buscar el objeto específico dentro de la lista
  const targetObject = list.find(item => item.field === targetField);

  // Si no se encuentra el objeto con ese field, por seguridad no mostramos la sección
  if (!targetObject) return false;

  // 3. Evaluar el flag correspondiente (show_view_preview o show_view_full) en el objeto encontrado
  return isPreviewMode 
    ? targetObject.show_view_preview === true 
    : targetObject.show_view_full === true;
};


/**
 * Valida únicamente si existen componentes configurados como visibles en la lista.
 * 
 * @param {Array} list - El arreglo (arrayListPrincipal o arrayListSecondary)
 * @param {boolean} isPreviewMode - true evalúa show_view_preview, false evalúa show_view_full
 * @returns {boolean} true si tiene al menos un hijo visible, false si no
 */
export const showAccordionSectionBySome = (list, isPreviewMode = false) => {
  //console.log("showAccordionSection, list, isPreviewMode", list, isPreviewMode);
  if (!list || list.length === 0) return false;

  // Evalúa si al menos un elemento de la lista tiene el flag correspondiente en true
  return list.some(item => 
    isPreviewMode ? item.show_view_preview === true : item.show_view_full === true
  );
};

/**
 * Valida si un arreglo de configuración contiene elementos con datos reales y visibles
 * para renderizar o no un Accordion.Item.
 * 
 * @param {Array} list - El arreglo de configuración (arrayListPrincipal o arrayListSecondary)
 * @param {Object} values - El estado con los datos reales del formulario
 * @param {Object} relationKeys - Objeto con las llaves de multimedias y problemas del módulo
 * @param {boolean} isPreviewMode - Indica si evalúa el modo Preview (true) o modo View (false)
 * @returns {boolean} True si hay datos reales para mostrar, False si debe ocultarse
 */
export const hasVisibleData = (list, values, relationKeys, isPreviewMode = false) => {
  if (!list || list.length === 0) return false;

  // 1. Filtrar la lista según el modo (Preview o View)
  const visibleItems = list.filter(item => 
    isPreviewMode ? item.show_view_preview : item.show_view_full
  );

  // Si no hay configurado ningún componente visible, ocultamos la sección
  if (visibleItems.length === 0) return false;

  // 2. Verificar si al menos uno de los componentes visibles tiene datos reales cargados
  return visibleItems.some(item => {
    // 🚫 Ignorar elementos que actúan solo como títulos/cabeceras de grupo
    if (item.component_view === "ViewGroupLabel" || item.type === "group") {
      return false; 
    }

    // 📸 Validación para el bloque de Multimedias
    if (item.component_view === "ViewTextGenericMultimedias") {
      const mediaKey = relationKeys?.multimedia;
      return values?.[mediaKey] && values[mediaKey].length > 0;
    }

    // 📝 Validación para el bloque de Problemas / Tablas
    if (item.component_view === "ViewTextGenericProblems") {
      const problemKey = relationKeys?.problems;
      return values?.[problemKey] && values[problemKey].length > 0;
    }

    // 🗂️ Validación para todos los inputs genéricos (Text, Catalog, Year, Date, etc.)
    const valueData = values?.[item.field];
    
    // Si la data del backend es un arreglo, validar que tenga elementos
    if (Array.isArray(valueData)) {
      return valueData.length > 0;
    }
    
    // Si es un texto, número o ID, verificar que no sea null, undefined o un string vacío
    return valueData !== undefined && valueData !== null && String(valueData).trim() !== "";
  });
};
